import React, { useRef, useEffect } from 'react';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
}

const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  length = 6,
  error = false,
  disabled = false,
  autoFocus = false,
  ariaLabel = 'Código de verificação',
}) => {
  const digits = Array.from({ length }, (_, i) => value[i] || '');
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus && refs.current[0]) {
      refs.current[0].focus();
    }
  }, [autoFocus]);

  const emit = (nextDigits: string[]) => {
    onChange(nextDigits.join('').slice(0, length));
  };

  const handleChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      const next = [...digits];
      next[index] = '';
      emit(next);
      return;
    }

    // Paste or multi-digit
    if (cleaned.length > 1) {
      const chars = cleaned.slice(0, length).split('');
      const next = Array.from({ length }, (_, i) => chars[i] || '');
      emit(next);
      const focusAt = Math.min(chars.length, length - 1);
      refs.current[focusAt]?.focus();
      return;
    }

    const next = [...digits];
    next[index] = cleaned;
    emit(next);
    if (index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const chars = pasted.split('');
    const next = Array.from({ length }, (_, i) => chars[i] || '');
    emit(next);
    refs.current[Math.min(chars.length, length) - 1]?.focus();
  };

  return (
    <div className="otp-row" role="group" aria-label={ariaLabel}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={el => {
            refs.current[index] = el;
          }}
          className={`otp-digit${error ? ' error' : ''}`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          value={digit}
          disabled={disabled}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          aria-label={`Dígito ${index + 1}`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
